import type { Instrument } from "$root/functional-areas/instruments-editor/const";
import { userClassificationsMap$ } from "$root/functional-areas/instruments/user-classifications";
import { registerPendingWrite } from "$root/functional-areas/user/pending-writes";
import { userStorage } from "$root/functional-areas/user/userStorage";
import { coalesce, seconds, tryJsonParse } from "$root/utils";
import { useDebounced } from "@mdotm/shadcn/legacy/react-extensions";
import BigNumber from "bignumber.js";
import { useUnsafeUpdatedRef } from "@mdotm/utils/react-extra";
import { getDefaultStore, readAtom, type RwAtom, useAtomValue, writeAtom } from "@mdotm/shadcn/legacy/reactivity";
import { instanceEqualsOrFastDeepEqual, type Setter } from "@mdotm/shadcn/legacy/utils";
import type {
	AgGridEvent,
	ColumnState,
	GridApi,
	IRowNode,
	RowSelectionOptions,
	ValueFormatterParams,
} from "ag-grid-community";
import type { AgGridReact, AgGridReactProps, CustomCellRendererProps } from "ag-grid-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { TagsAsBadges } from "./custom-column/tags/tag-views";
import type { Versioned } from "$root/third-party-integrations/zod";
import { zAgGridColStateV1 } from "./col-state-schema";

const selectionColumnId = "ag-Grid-SelectionColumn";
const autoGroupColumnId = "ag-Grid-AutoColumn";

/**
 * Checks every row covered by a settled cell selection scoped to the column carrying the
 * checkboxes, so dragging down that column ticks the rows it covered once released. Union
 * semantics: this only ever checks — unchecking stays on the checkbox itself. AG Grid keeps cell
 * selection and row selection separate (its only built-in cross-over is shift-click extending
 * the row selection), hence the manual bridge.
 *
 * `carriesCheckbox` decides which columns count, because the checkbox isn't always in a column
 * of its own — see `checkboxColumn` below.
 *
 * Deliberately ignores a selection of a single row, which leaves a plain click in the column
 * doing what it always did (nothing).
 *
 * A plain click on the checkbox itself never gets here, so it still toggles as usual:
 * `CellMouseListenerFeature.onMouseDown` bails out on `.ag-selection-checkbox` before creating
 * a range. Starting a *drag* there is fine — the range is picked up on the first move.
 */
function selectRowsCoveredByCheckboxCellSelection<T>(
	api: GridApi<T>,
	carriesCheckbox: (colId: string) => boolean,
): void {
	// Ranges can overlap, so count the rows covered rather than summing range heights.
	const coveredRowIndexes = new Set<number>();
	for (const { startRow, endRow, columns } of api.getCellRanges() ?? []) {
		// Pinned rows (e.g. the footer) have no checkbox to tick.
		if (!startRow || !endRow || startRow.rowPinned != null || endRow.rowPinned != null) {
			continue;
		}
		if (!columns.every((column) => carriesCheckbox(column.getColId()))) {
			continue;
		}
		const from = Math.min(startRow.rowIndex, endRow.rowIndex);
		const to = Math.max(startRow.rowIndex, endRow.rowIndex);
		for (let rowIndex = from; rowIndex <= to; rowIndex++) {
			coveredRowIndexes.add(rowIndex);
		}
	}
	if (coveredRowIndexes.size < 2) {
		return;
	}
	const nodes: IRowNode<T>[] = [];
	for (const rowIndex of coveredRowIndexes) {
		const node = api.getDisplayedRowAtIndex(rowIndex);
		if (node?.selectable && !node.isSelected()) {
			nodes.push(node);
		}
	}
	if (nodes.length > 0) {
		api.setNodesSelected({ nodes, newValue: true, source: "checkboxSelected" });
	}
}

function checkboxColumn<T>(params?: RowSelectionOptions<T>): Partial<AgGridReactProps<T>> {
	// Which column the drag-to-check gesture applies to. Normally the grid's own selection
	// column, but with `checkboxLocation: "autoGroupColumn"` the checkbox is rendered inside the
	// group column, mixed in with the group/instrument name, and no separate selection column is
	// generated. That swap is conditional on grouping being active
	// (`SelectionColService.isSelectionColumnEnabled` only stands the selection column down while
	// auto columns exist), so both ids have to qualify for the grouped and ungrouped views of the
	// same table to behave alike. Matched by prefix like AG Grid's own `isColumnSelectionCol` /
	// `isColumnGroupAutoCol`, since `groupDisplayType: "multipleColumns"` suffixes the auto col.
	const checkboxLivesInGroupColumn = params?.checkboxLocation === "autoGroupColumn";
	const carriesCheckbox = (colId: string) =>
		colId.startsWith(selectionColumnId) || (checkboxLivesInGroupColumn && colId.startsWith(autoGroupColumnId));
	return {
		// Only when a multi-event interaction ends: `finished` is false for every cell a drag
		// passes over, and `started` marks the interactions that begin and end in one event.
		// Both flags are load-bearing:
		// - `finished` alone ticked rows mid-drag — a re-render per row (visibly laggy on a big
		//   table) and no chance to pull a too-wide drag back before it committed.
		// - `!started` skips the single-event interactions, which are the ones that act on a
		//   *stale* range: AG Grid keeps the previous range alive, so a ctrl+click appending a
		//   1x1 range (`setRangeToCell`) or a shift-click growing the last one
		//   (`updateRangeRowBoundary`) would satisfy the row count below with rows left over
		//   from an earlier selection — committing at press, and re-checking rows the user had
		//   deliberately unchecked in between. Drag-stop and keyboard extension both report
		//   `(started: false, finished: true)`, so the gestures that matter still land.
		// The cost is that shift-clicking the cell *padding* no longer bridges to row
		// selection; shift-clicking the checkbox itself still does, via AG Grid's own
		// `selectionCtx.extend`.
		onCellSelectionChanged: (e) => {
			if (e.finished && !e.started) {
				selectRowsCoveredByCheckboxCellSelection(e.api, carriesCheckbox);
			}
		},
		rowSelection: {
			...coalesce(params, {
				mode: "multiRow",
				selectAll: "currentPage",
				// enableClickSelection: true,
				// checkboxLocation: "autoGroupColumn",
				// groupSelects: "descendants",
			}),
		},
		selectionColumnDef: {
			pinned: "left",
			lockPosition: true,
			cellClassRules: {
				"hidden!": (cellParams) => {
					return Boolean(
						cellParams.node.rowPinned === "bottom" &&
							cellParams.column.getColId() === "ag-Grid-SelectionColumn" &&
							cellParams.node.footer,
					);
				},
			},
		},
	};
}

function usePersistColState(storageKey: string): {
	maintainColumnOrder: boolean;
	onStateUpdated: (e: AgGridEvent) => void;
	onGridReady: (e: AgGridEvent) => void;
} {
	const lastPersistedColStateRef = useRef<unknown>(null);
	const debouncedColumnPersistence = useDebounced(
		(colState: ColumnState[], key: string) => {
			const current = colState.map((orig) => {
				const col = { ...orig };
				delete col.aggFunc;
				return col;
			});
			if (instanceEqualsOrFastDeepEqual(lastPersistedColStateRef.current, current)) {
				return;
			}
			lastPersistedColStateRef.current = current;
			userStorage.set(
				key,
				JSON.stringify({
					version: 1,
					data: current,
				} satisfies Versioned<ColumnState[]>),
			);
		},
		{ debounceInterval: seconds(1), unmountBehavior: "flush" },
	);

	const debouncedColumnPersistenceRef = useUnsafeUpdatedRef(debouncedColumnPersistence);

	// The layout the user is in the middle of changing stays unsent for up to a second, and the
	// identity it belongs to can change inside that second. Registering the flush is what lets an
	// identity swap wait for it rather than deliver it as the arriving user. See `settlePendingWrites`.
	useEffect(
		() => registerPendingWrite({ flush: () => debouncedColumnPersistenceRef.current.flush() }),
		[debouncedColumnPersistenceRef],
	);

	return useMemo(
		() => ({
			// Prevents ag-grid from resetting column order when columnDefs changes
			maintainColumnOrder: true,
			onStateUpdated: (event) => debouncedColumnPersistenceRef.current.invoke(event.api.getColumnState(), storageKey),
			onGridReady: (e) => {
				const columnsStorage = userStorage.get(storageKey);
				if (!columnsStorage) {
					return;
				}

				const parsedState = tryJsonParse(columnsStorage, null, (x) => zAgGridColStateV1().parse(x));
				if (!parsedState) {
					return;
				}

				e.api.resetColumnGroupState();
				e.api.applyColumnState({
					state: parsedState.data,
					applyOrder: true,
				});
			},
		}),
		[debouncedColumnPersistenceRef, storageKey],
	);
}

function useGroupByClassification(
	agGridEl: AgGridReact<any> | null,
): [string | null | undefined, Setter<string | null>] {
	const [groupId, _setGroupId] = useState<string | null | undefined>(undefined);

	const classifications = useAtomValue(userClassificationsMap$)!;

	useEffect(() => {
		if (!agGridEl || !groupId) {
			agGridEl?.api.setGridOption("autoGroupColumnDef", undefined);
			return;
		}
		agGridEl.api.setGridOption("autoGroupColumnDef", {
			colId: "e00d9d4c-f77e-46c4-8691-a27549d9311e",
			pinned: "left",
			minWidth: 200,
			headerName: "Name" /* classifications.get(groupId)?.name, */,
			// Instrument rows keep no value here, so sorting this column leaves them in their original order;
			// the formatter alone hands their name to copy and export, which read formatted text.
			valueFormatter: (params: ValueFormatterParams<Instrument>) => params.data?.name ?? "",
			cellRendererParams: {
				innerRenderer(params: CustomCellRendererProps<Instrument>) {
					if (Array.isArray(params.value)) {
						if (params.value.length > 0) {
							return (
								<TagsAsBadges
									style={{
										height: "var(--ag-internal-content-line-height)",
									}}
									viewMode={
										classifications.get(groupId)?.dataVisualization === "TAG_VIEW_WITHOUT_COLOR"
											? "TAG_VIEW_WITHOUT_COLOR"
											: "TAG_VIEW_WITH_COLOR"
									}
									options={classifications.get(groupId)?.options ?? []}
									values={params.value}
								/>
							);
						}
						return "Ungrouped";
					}
					return params.data?.name;
				},
			},
			headerClass: "pl-[44px]",
			cellClassRules: {
				"[&>span>span.ag-group-value]:opacity-50": (params) => Boolean(params.data?.deleted),
			},
		});
	}, [agGridEl, classifications, groupId]);

	// AG Grid re-applies stateful colDef attributes (hide/pinned) to existing columns whenever
	// columnDefs are rebuilt, which would resurrect the NAME column next to the auto group column.
	// Re-assert the grouped layout after every columns load (also heals persisted states saved
	// while the two columns were both visible).
	useEffect(() => {
		if (!agGridEl || !groupId) {
			return;
		}
		const api = agGridEl.api;
		function hideNameColumn() {
			api.applyColumnState({ state: [{ colId: "NAME", hide: true, pinned: false }] });
		}
		hideNameColumn();
		api.addEventListener("newColumnsLoaded", hideNameColumn);
		return () => {
			if (!api.isDestroyed()) {
				api.removeEventListener("newColumnsLoaded", hideNameColumn);
			}
		};
	}, [agGridEl, groupId]);

	useEffect(() => {
		if (agGridEl && classifications) {
			const col = agGridEl.api.getColumnState().find((c) => c.rowGroup);
			const candidateGroupId = col ? (classifications.has(col.colId) ? col.colId : null) : null;
			_setGroupId((g) => (g === undefined ? candidateGroupId : g));
		}
	}, [classifications, agGridEl]);

	const refs = useUnsafeUpdatedRef({ agGridEl, groupId });
	const setGroupId = useCallback(
		(v: string | null) => {
			if (!refs.current.agGridEl) {
				return;
			}
			flushSync(() => _setGroupId(v)); // we need to make sure that setGridOption above is run before updating the columns
			const clonedColumns = [...refs.current.agGridEl.api.getColumnState()];
			for (let i = 0; i < clonedColumns.length; i++) {
				clonedColumns[i] = {
					...clonedColumns[i],
					rowGroupIndex: undefined,
					rowGroup: false,
					// restore visibility of previously grouped column
					hide: clonedColumns[i].rowGroup ? false : clonedColumns[i].hide,
				};
			}

			const nameColumnIndex = clonedColumns.findIndex((c) => c.colId === "NAME");

			const groupColumnIndex = refs.current.groupId
				? clonedColumns.findIndex((c) => c.colId === refs.current.groupId)
				: -1;

			if (nameColumnIndex !== -1) {
				if (v) {
					clonedColumns[nameColumnIndex] = {
						...clonedColumns[nameColumnIndex],
						pinned: false,
						hide: true,
					};
				} else {
					clonedColumns[nameColumnIndex] = {
						...clonedColumns[nameColumnIndex],
						pinned: "left",
						hide: false,
					};
				}
			}
			if (groupColumnIndex !== -1) {
				clonedColumns[groupColumnIndex] = {
					...clonedColumns[groupColumnIndex],
					rowGroup: true,
					hide: true,
				};
			}

			if (!refs.current.groupId) {
				const checkboxColumnIndex = clonedColumns.findIndex((col) => col.colId === "ag-Grid-AutoColumn");
				if (checkboxColumnIndex !== -1) {
					const clonedCheckbox = { ...clonedColumns[checkboxColumnIndex] };
					clonedColumns.splice(checkboxColumnIndex, 1);
					clonedColumns.unshift({
						...clonedCheckbox,
						colId: "ag-Grid-SelectionColumn",
						pinned: "left",
						width: 50,
					});
				}
			}

			refs.current.agGridEl.api.resetColumnGroupState();
			refs.current.agGridEl.api.applyColumnState({
				state: clonedColumns,
				applyOrder: true,
			});
		},
		[refs],
	);

	return useMemo(() => [groupId, setGroupId], [groupId, setGroupId]);
}

/**
 * Summed exactly, like the editor's own total: a float sum drifts in the last decimals, so at 14 decimals
 * the total row would contradict the banner that checks the total against 100.
 */
function sumNonDeletedWeights<T extends { weight?: number; deleted?: boolean }>(nodes: Iterable<IRowNode<T>>): number {
	let sum = BigNumber(0);
	for (const node of nodes) {
		if (!node.data?.deleted) {
			sum = sum.plus(node.data?.weight ?? 0);
		}
	}
	return sum.toNumber();
}

function aggFunctions<T extends { weight?: number; deleted?: boolean }>(): Partial<AgGridReactProps<T>> {
	return {
		aggFuncs: {
			sumWeights(params) {
				if (params.rowNode.group) {
					return sumNonDeletedWeights(params.rowNode.allLeafChildren ?? []);
				}

				const nodes: IRowNode<T>[] = [];
				params.api.forEachNode((node) => nodes.push(node));
				return sumNonDeletedWeights(nodes);
			},
		},
	};
}

function useSyncColStateWithAtom(atom$: RwAtom<ColumnState[] | null>): {
	onStateUpdated: (e: AgGridEvent) => void;
	onGridReady: (e: AgGridEvent) => void;
} {
	const refs = useRef({
		agGridApi: null as GridApi | null,
		lastPersistedColState: null as unknown,
	});

	function updateAgGrid() {
		const columnsStorage = readAtom(atom$);
		if (!columnsStorage) {
			return;
		}

		const api = refs.current.agGridApi;
		if (!api) {
			return;
		}

		// Skip when applying columnsStorage would be a no-op for the columns we manage — i.e. the grid
		// already reflects every field we'd set, in the same relative order. Grid-originated changes
		// (resize/move) write back through atom$; without this guard updateAgGrid would re-apply them
		// mid-drag and cause jitter / re-render churn. (atom$ holds a subset of the grid's columns, so
		// a full getColumnState() comparison would never match — compare per managed column instead.)
		const currentState = api.getColumnState();
		const currentByColId = new Map(currentState.map((col) => [col.colId, col]));
		const managedColIds = columnsStorage.map((col) => col.colId);
		const currentManagedOrder = currentState.filter((col) => managedColIds.includes(col.colId)).map((col) => col.colId);
		const sameOrder = instanceEqualsOrFastDeepEqual(managedColIds, currentManagedOrder);
		const sameState = columnsStorage.every((col) => {
			const current = currentByColId.get(col.colId);
			return current != null && instanceEqualsOrFastDeepEqual(current, { ...current, ...col });
		});
		if (sameOrder && sameState) {
			return;
		}

		api.resetColumnGroupState();
		api.applyColumnState({
			state: columnsStorage,
			applyOrder: true,
		});
	}

	const updateAgGridRef = useUnsafeUpdatedRef(updateAgGrid);

	useEffect(() => {
		const unsub = getDefaultStore().sub(atom$, () => updateAgGridRef.current());
		return unsub;
	}, [updateAgGridRef, atom$]);

	return {
		onStateUpdated: (e) => {
			refs.current.agGridApi = e.api;

			const current = e.api.getColumnState().map((orig) => {
				const col = { ...orig };
				delete col.aggFunc;
				return col;
			});
			if (instanceEqualsOrFastDeepEqual(refs.current.lastPersistedColState, current)) {
				return;
			}
			refs.current.lastPersistedColState = current;
			writeAtom(atom$, current);
		},
		onGridReady: (e) => {
			refs.current.agGridApi = e.api;
		},
	};
}

export const agExtra = {
	checkboxColumn,
	usePersistColState,
	useGroupByClassification,
	aggFunctions,
	useSyncColStateWithAtom,
};
